let musicOn = true;
// Music
export let audio = {
  playbackRate: 1,
  volume: 1
};
export let musicContinue = false;
export let isPlaying = false;
let stopPrevTransition = false;
let stopPrevTransitionSpeed = false;
let intervalBetween;
let saveSrc;
let saveLoopTime;
let saveSettings;

const AudioCtxClass = window.AudioContext || window.webkitAudioContext;
let audioCtx = null;
let gainNode = null;
let currentSource = null;
let decodedBuffer = null;
let startedAt = 0;
let pausedAt = 0;
let lastPlaybackRate = 1;

export function musicOnSet(value){
  musicOn = value;
}

export function musicContinueSwitch(value){
  if(!musicOn){
    return;
  }
  musicContinue = value;
}

function waitingFunc(t){
  return new Promise((resolve) => {
    setTimeout(resolve, t);
  });
}

function base64ToArrayBuffer(base64) {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

async function ensureAudioContext() {
  if (!audioCtx) {
    audioCtx = new AudioCtxClass();
    gainNode = audioCtx.createGain();
    gainNode.gain.value = audio.volume != null ? audio.volume : 1;
    gainNode.connect(audioCtx.destination);
  }
}

function createAndStartSource(offset = 0, when = 0, rate = 1) {
  if (!decodedBuffer) return null;
  if (currentSource) {
    try { currentSource.onended = null; currentSource.stop(0); } catch (e) {}
    currentSource = null;
  }
  const src = audioCtx.createBufferSource();
  src.buffer = decodedBuffer;
  src.playbackRate.value = rate;
  src.connect(gainNode);
  src.onended = () => {
    if (!isPlaying) return;
    if (saveLoopTime) {
      intervalBetween = setTimeout(restartLoop, saveLoopTime);
      function restartLoop() {
        setVolume(0);
        playMusic(saveSrc, saveLoopTime);
        if (saveSettings && saveSettings[0] != null) {
          transitionVolume(saveSettings[0], saveSettings[1]);
        }
      }
    }
    isPlaying = false;
  };
  try {
    src.start(when, offset);
  } catch (e) {
    console.error('createAndStartSource start failed', e);
  }
  currentSource = src;
  startedAt = audioCtx.currentTime - offset / rate;
  lastPlaybackRate = rate;
  isPlaying = true;
  return src;
}


export async function playMusic(track, loopTime, startTime = 0) {
  if(!musicOn){
    return;
  }
  if (!track || !track.base64) return;
  if (musicContinue) return;

  saveSrc = track;
  if (loopTime) {
    saveLoopTime = loopTime;
    clearTimeout(intervalBetween);
    intervalBetween = undefined;
  }

  await ensureAudioContext();

  try {
    const arrayBuffer = base64ToArrayBuffer(track.base64);
    decodedBuffer = await audioCtx.decodeAudioData(arrayBuffer.slice(0));
    pausedAt = 0;
    const start = Math.max(0, Number(startTime) || 0);
    createAndStartSource(start, 0, audio.playbackRate || 1);
    pausedAt = start;
  } catch (e) {
    console.error('Music error', e);
  }
}

export function changeMusicSpeed(speed) {
  if(!musicOn){
    return;
  }
  const clamped = Math.max(0.1, Math.min(4, speed));
  audio.playbackRate = clamped;
  if (currentSource) {
    try {
      const wasPlaying = isPlaying;
      const currentPos = getCurrentPosition();
      try { currentSource.onended = null; currentSource.stop(0); } catch (e) {}
      currentSource = null;
      createAndStartSource(currentPos, 0, clamped);
      if (!wasPlaying) {
        try { currentSource.onended = null; currentSource.stop(0); } catch (e) {}
        currentSource = null;
        isPlaying = false;
        pausedAt = currentPos;
      }
    } catch (e) {
      console.error('changeMusicSpeed error', e);
    }
  }
}

function getCurrentPosition() {
  if (!audioCtx || !decodedBuffer) return 0;
  if (!currentSource) return pausedAt || 0;
  const rate = lastPlaybackRate ?? audio.playbackRate ?? 1;
  const pos = (audioCtx.currentTime - startedAt) * rate;
  return Math.max(0, Math.min(decodedBuffer.duration, pos));
}

export function pauseMusic() {
  if(!musicOn){
    return;
  }
  if (!audioCtx) return;
  if (!isPlaying) return;
  pausedAt = getCurrentPosition();
  try { currentSource.onended = null; currentSource.stop(0); } catch (e) {}
  currentSource = null;
  isPlaying = false;
}

export function resumeMusic() {
  if(!musicOn){
    return;
  }
  if (!audioCtx || !decodedBuffer) return;
  if (isPlaying) return;
  createAndStartSource(pausedAt, 0, audio.playbackRate || 1);
}

export function stopMusic() {
  if(!musicOn){
    return;
  }
  if (currentSource) {
    try { currentSource.onended = null; currentSource.stop(0); } catch (e) {}
    currentSource = null;
  }
  clearTimeout(intervalBetween);
  intervalBetween = undefined;
  saveLoopTime = false;
  saveSrc = false;
  saveSettings = false;
  pausedAt = 0;
  isPlaying = false;
}

export function setVolume(value) {
  if(!musicOn){
    return;
  }
  const v = Math.max(0, Math.min(1, value));
  audio.volume = v;
  if (gainNode) {
    gainNode.gain.setValueAtTime(v, audioCtx.currentTime);
  }
}

export async function transitionVolume(targetVolume = 1, duration = 2000) {
  return new Promise(async (resolve) => {
    if(!musicOn){
      resolve();
      return;
    }
    stopPrevTransition = true;
    await waitingFunc(100);
    stopPrevTransition = false;
    targetVolume = Math.max(0, Math.min(1, targetVolume));
    const steps = 30;
    const interval = duration / steps;
    const start = audio.volume != null ? audio.volume : (gainNode ? gainNode.gain.value : 1);
    const step = (targetVolume - start) / steps;
    saveSettings = [targetVolume, duration];
    for (let i = 0; i < steps; i++) {
      const newV = Math.max(0, Math.min(1, start + step * (i + 1)));
      setVolume(newV);
      if (stopPrevTransition) {
        resolve();
        return;
      }
      await waitingFunc(interval);
    }
    setVolume(targetVolume);
    resolve();
  });
}

export async function transitionSpeed(targetSpeed = 1, duration = 2000) {
  return new Promise(async (resolve) => {
    if(!musicOn){
      resolve();
      return;
    }
    stopPrevTransitionSpeed = true;
    await waitingFunc(100);
    stopPrevTransitionSpeed = false;
    targetSpeed = Math.max(0.1, Math.min(4, targetSpeed));

const current = audio.playbackRate || 1;
    const steps = 30;
    const interval = duration / steps;
    const step = (targetSpeed - current) / steps;
    for (let i = 0; i < steps; i++) {
      const newSpeed = Math.max(0.1, Math.min(4, current + step * (i + 1)));
      changeMusicSpeed(newSpeed);
      if (stopPrevTransitionSpeed) {
        resolve();
        return;
      }
      await waitingFunc(interval);
    }
    changeMusicSpeed(targetSpeed);
    resolve();
  });
}

//Ambient
export let ambient = {
  playbackRate: 1,
  volume: 1
};
export let isPlayingAmbient = false;
let stopPrevTransitionAmbient = false;
let stopPrevTransitionSpeedAmbient = false;
let intervalBetweenAmbient;
let saveSrcAmbient;
let saveLoopTimeAmbient;
let saveSettingsAmbient;

let ambientCtx = null;
let ambientGain = null;
let ambientSource = null;
let ambientDecodedBuffer = null;
let ambientStartedAt = 0;
let ambientPausedAt = 0;
let ambientLastPlaybackRate = 1;

async function ensureAmbientContext() {
  if (!ambientCtx) {
    ambientCtx = new AudioCtxClass();
    ambientGain = ambientCtx.createGain();
    ambientGain.gain.value = ambient.volume != null ? ambient.volume : 1;
    ambientGain.connect(ambientCtx.destination);
  }
  try {
    if (ambientCtx.state === 'suspended') {
      await ambientCtx.resume();
    }
  } catch (e) {
  }
}

function getAmbientCurrentPosition() {
  try {
    if (!ambientCtx || !ambientDecodedBuffer) return 0;
    if (!ambientSource) return ambientPausedAt || 0;
    const rate = ambientLastPlaybackRate != null ? ambientLastPlaybackRate : (ambient.playbackRate != null ? ambient.playbackRate : 1);
    const pos = (ambientCtx.currentTime - ambientStartedAt) * rate;
    return Math.max(0, Math.min(ambientDecodedBuffer.duration, pos));
  } catch (e) {
    return 0;
  }
}

function createAndStartAmbientSource(offset = 0, when = 0, rate = 1, loop = false) {
  if (!ambientDecodedBuffer) return null;
  if (ambientSource) {
    try { ambientSource.onended = null; ambientSource.stop(0); } catch (e) {}
    ambientSource = null;
  }
  const src = ambientCtx.createBufferSource();
  src.buffer = ambientDecodedBuffer;
  src.playbackRate.value = rate;
  src.loop = !!loop;
  src.connect(ambientGain);
  src.onended = () => {
    if (src.loop) return;
    if (saveLoopTimeAmbient) {
      intervalBetweenAmbient = setTimeout(restartAmbientLoop, saveLoopTimeAmbient);
      function restartAmbientLoop() {
        setVolumeAmbient(0);
        playAmbient(saveSrcAmbient, saveLoopTimeAmbient);
        if (saveSettingsAmbient && saveSettingsAmbient[0] != null) {
          transitionVolumeAmbient(saveSettingsAmbient[0], saveSettingsAmbient[1]);
        }
      }
    }
    isPlayingAmbient = false;
  };
  try {
    src.start(when, offset);
  } catch (e) {
    console.error('ambient start failed', e);
  }
  ambientSource = src;
  ambientStartedAt = ambientCtx.currentTime - offset / rate;
  ambientLastPlaybackRate = rate;
  isPlayingAmbient = true;
  return src;
}

export async function playAmbient(track, loopTime, startTime = 0) {
  if(!musicOn || !track){
    return;
  }
  if (typeof musicContinue !== 'undefined' && musicContinue) return;

  saveSrcAmbient = track;
  if (loopTime) {
    saveLoopTimeAmbient = loopTime;
    clearTimeout(intervalBetweenAmbient);
    intervalBetweenAmbient = undefined;
  }

  await ensureAmbientContext();

  try {
    const arrayBuffer = base64ToArrayBuffer(track.base64);
    ambientDecodedBuffer = await ambientCtx.decodeAudioData(arrayBuffer.slice(0));
    ambientPausedAt = 0;
    const start = Math.max(0, Number(startTime) || 0);
    createAndStartAmbientSource(start, 0, ambient.playbackRate || 1, false);
    ambientPausedAt = start;
  } catch (e) {
    console.error('Ambient error', e);
  }
}

export function changeAmbientSpeed(speed) {
  if(!musicOn){
    return;
  }
  const clamped = Math.max(0.1, Math.min(4, speed));
  ambient.playbackRate = clamped;
  if (ambientSource) {
    try {
      const wasPlaying = isPlayingAmbient;
      const currentPos = getAmbientCurrentPosition();
      try { ambientSource.onended = null; ambientSource.stop(0); } catch (e) {}
      ambientSource = null;
      createAndStartAmbientSource(currentPos, 0, clamped, ambientSource ? ambientSource.loop : false);
      if (!wasPlaying) {
        try { ambientSource.onended = null; ambientSource.stop(0); } catch (e) {}
        ambientSource = null;
        isPlayingAmbient = false;
        ambientPausedAt = currentPos;
      }
    } catch (e) {
      console.error('changeAmbientSpeed error', e);
    }
  }
}

export function pauseAmbient() {
  if(!musicOn){
    return;
  }
  if (!ambientCtx) return;
  if (!isPlayingAmbient) return;
  ambientPausedAt = getAmbientCurrentPosition();
  try { ambientSource.onended = null; ambientSource.stop(0); } catch (e) {}
  ambientSource = null;
  isPlayingAmbient = false;
}

export function resumeAmbient() {
  if(!musicOn){
    return;
  }
  if (!ambientCtx || !ambientDecodedBuffer) return;
  if (isPlayingAmbient) return;
  createAndStartAmbientSource(ambientPausedAt, 0, ambient.playbackRate || 1);
}


export function stopAmbient() {
  if(!musicOn){
    return;
  }
  if (ambientSource) {
    try { ambientSource.onended = null; ambientSource.stop(0); } catch (e) {}
    ambientSource = null;
  }
  clearTimeout(intervalBetweenAmbient);
  intervalBetweenAmbient = undefined;
  saveLoopTimeAmbient = false;
  saveSrcAmbient = false;
  saveSettingsAmbient = false;
  ambientPausedAt = 0;
  isPlayingAmbient = false;
}

export function setVolumeAmbient(value) {
  if(!musicOn){
    return;
  }
  const v = Math.max(0, Math.min(1, value));
  ambient.volume = v;
  if (ambientGain && ambientCtx) {
    ambientGain.gain.setValueAtTime(v, ambientCtx.currentTime);
  }
}

export async function transitionVolumeAmbient(targetVolume = 1, duration = 2000) {
  return new Promise(async (resolve) => {
    if(!musicOn){
      resolve();
      return;
    }
    stopPrevTransitionAmbient = true;
    await waitingFunc(100);
    stopPrevTransitionAmbient = false;
    targetVolume = Math.max(0, Math.min(1, targetVolume));
    const steps = 30;
    const interval = duration / steps;
    const start = ambient.volume != null ? ambient.volume : (ambientGain ? ambientGain.gain.value : 1);
    const step = (targetVolume - start) / steps;
    saveSettingsAmbient = [targetVolume, duration];
    for (let i = 0; i < steps; i++) {
      const newV = Math.max(0, Math.min(1, start + step * (i + 1)));
      setVolumeAmbient(newV);
      if (stopPrevTransitionAmbient) {
        resolve();
        return;
      }
      await waitingFunc(interval);
    }
    setVolumeAmbient(targetVolume);
    resolve();
  });
}

export async function transitionSpeedAmbient(targetSpeed = 1, duration = 2000) {
  return new Promise(async (resolve) => {
    if(!musicOn){
      resolve();
      return;
    }
    stopPrevTransitionSpeedAmbient = true;
    await waitingFunc(100);
    stopPrevTransitionSpeedAmbient = false;
    targetSpeed = Math.max(0.1, Math.min(4, targetSpeed));
    const current = ambient.playbackRate || 1;
    const steps = 30;
    const interval = duration / steps;
    const step = (targetSpeed - current) / steps;
    for (let i = 0; i < steps; i++) {
      const newSpeed = Math.max(0.1, Math.min(4, current + step * (i + 1)));
      changeAmbientSpeed(newSpeed);
      if (stopPrevTransitionSpeedAmbient) {
        resolve();
        return;
      }
      await waitingFunc(interval);
    }
    changeAmbientSpeed(targetSpeed);
    resolve();
  });
}

// Effects
export let effects = {
  playbackRate: 1,
  volume: 1
};
export let isPlayingEffects = false;

let effectsCtx = null;
let effectsGain = null;
let effectsSource = null;
let effectsDecodedBuffer = null;
let effectsStartedAt = 0;
let effectsPausedAt = 0;
let effectsLastPlaybackRate = 1;

async function ensureEffectsContext() {
  if (!effectsCtx) {
    effectsCtx = new AudioCtxClass();
    effectsGain = effectsCtx.createGain();
    effectsGain.gain.value = effects.volume != null ? effects.volume : 1;
    effectsGain.connect(effectsCtx.destination);
  }
}

function getEffectsCurrentPosition() {
  try {
    if (!effectsCtx || !effectsDecodedBuffer) return 0;
    if (!effectsSource) return effectsPausedAt || 0;
    const rate = effectsLastPlaybackRate != null ? effectsLastPlaybackRate : (effects.playbackRate != null ? effects.playbackRate : 1);
    const pos = (effectsCtx.currentTime - effectsStartedAt) * rate;
    return Math.max(0, Math.min(effectsDecodedBuffer.duration, pos));
  } catch (e) {
    return 0;
  }
}

function createAndStartEffectsSource(offset = 0, when = 0, rate = 1, loop = false) {
  if (!effectsDecodedBuffer) return null;
  if (effectsSource) {
    try { effectsSource.onended = null; effectsSource.stop(0); } catch (e) {}
    effectsSource = null;
  }
  const src = effectsCtx.createBufferSource();
  src.buffer = effectsDecodedBuffer;
  src.playbackRate.value = rate;
  src.loop = !!loop;
  src.connect(effectsGain);
  src.onended = () => {
    if (src.loop) return;
    isPlayingEffects = false;
  };
  try {
    src.start(when, offset);
  } catch (e) {
    console.error('effects start failed', e);
  }
  effectsSource = src;
  effectsStartedAt = effectsCtx.currentTime - offset / rate;
  effectsLastPlaybackRate = rate;
  isPlayingEffects = true;
  return src;
}

export async function playEffects(track, loop = true) {
  if(!musicOn || !track){
    return;
  }

  await ensureEffectsContext();

  try {
    const ab = base64ToArrayBuffer(track.base64);
    effectsDecodedBuffer = await effectsCtx.decodeAudioData(ab.slice(0));
    effectsPausedAt = 0;
    createAndStartEffectsSource(0, 0, effects.playbackRate || 1, loop);
  } catch (e) {
    console.error('Effects error', e);
  }
}

export function pauseEffects() {
  if (!musicOn){
   return;
 }
  if (!effectsCtx) return;
  if (!isPlayingEffects) return;
  effectsPausedAt = getEffectsCurrentPosition();
  try { effectsSource.onended = null; effectsSource.stop(0); } catch (e) {}
  effectsSource = null;
  isPlayingEffects = false;
}

export function resumeEffects() {
  if (!musicOn){
    return;
  }
  if (!effectsCtx || !effectsDecodedBuffer) return;
  if (isPlayingEffects) return;
  createAndStartEffectsSource(effectsPausedAt, 0, effects.playbackRate || 1, effectsSource ? effectsSource.loop : false);
}


export function stopEffects() {
  if(!musicOn){
    return;
  }
  if (effectsSource) {
    try { effectsSource.onended = null; effectsSource.stop(0); } catch (e) {}
    effectsSource = null;
  }
  effectsPausedAt = 0;
  isPlayingEffects = false;
}

export function setVolumeEffects(value) {
  if(!musicOn){
    return;
  }
  const v = Math.max(0, Math.min(1, value));
  effects.volume = v;
  if (effectsGain && effectsCtx) {
    effectsGain.gain.setValueAtTime(v, effectsCtx.currentTime);
  }
}

export async function transitionVolumeEffects(targetVolume = 1, duration = 2000) {
  return new Promise(async (resolve) => {
    if(!musicOn){
      resolve();
      return;
    }
    targetVolume = Math.max(0, Math.min(1, targetVolume));
    const steps = 30;
    const interval = duration / steps;
    const start = effects.volume != null ? effects.volume : (effectsGain ? effectsGain.gain.value : 1);
    const step = (targetVolume - start) / steps;
    for (let i = 0; i < steps; i++) {
      const newV = Math.max(0, Math.min(1, start + step * (i + 1)));
      setVolumeEffects(newV);
      await waitingFunc(interval);
    }
    setVolumeEffects(targetVolume);
    resolve();
  });
}

// Effects VN
export let effectsVN = {
  playbackRate: 1,
  volume: 1
};
export let isPlayingEffectsVN = false;

let effectsVNCtx = null;
let effectsVNGain = null;
let effectsVNSource = null;
let effectsVNDecodedBuffer = null;
let effectsVNStartedAt = 0;
let effectsVNPauseAt = 0;
let effectsVNLastPlaybackRate = 1;

async function ensureEffectsVNCtx() {
  if (!effectsVNCtx) {
    effectsVNCtx = new AudioCtxClass();
    effectsVNGain = effectsVNCtx.createGain();
    effectsVNGain.gain.value = effectsVN.volume != null ? effectsVN.volume : 1;
    effectsVNGain.connect(effectsVNCtx.destination);
  }
}

function getEffectsVNCurrentPosition() {
  try {
    if (!effectsVNCtx || !effectsVNDecodedBuffer) return 0;
    if (!effectsVNSource) return effectsVNPauseAt || 0;
    const rate = effectsVNLastPlaybackRate != null ? effectsVNLastPlaybackRate : (effectsVN.playbackRate != null ? effectsVN.playbackRate : 1);
    const pos = (effectsVNCtx.currentTime - effectsVNStartedAt) * rate;
    return Math.max(0, Math.min(effectsVNDecodedBuffer.duration, pos));
  } catch (e) {
    return 0;
  }
}

function createAndStartEffectsVNSource(offset = 0, when = 0, rate = 1, loop = false) {
  if (!effectsVNDecodedBuffer) return null;
  if (effectsVNSource) {
    try { effectsVNSource.onended = null; effectsVNSource.stop(0); } catch (e) {}
    effectsVNSource = null;
  }
  const src = effectsVNCtx.createBufferSource();
  src.buffer = effectsVNDecodedBuffer;
  src.playbackRate.value = rate;
  src.loop = !!loop;
  src.connect(effectsVNGain);
  src.onended = () => {
    if (src.loop) return;
    isPlayingEffectsVN = false;
  };
  try {
    src.start(when, offset);
  } catch (e) {
    console.error('effectsVN start failed', e);
  }
  effectsVNSource = src;
  effectsVNStartedAt = effectsVNCtx.currentTime - offset / rate;
  effectsVNLastPlaybackRate = rate;
  isPlayingEffectsVN = true;
  return src;
}


export async function playEffectsVN(track, loop = true) {
  if(!musicOn || !track){
    return;
  }

  await ensureEffectsVNCtx();

  try {
    const ab = base64ToArrayBuffer(track.base64);
    effectsVNDecodedBuffer = await effectsVNCtx.decodeAudioData(ab.slice(0));
    effectsVNPauseAt = 0;
    createAndStartEffectsVNSource(0, 0, effectsVN.playbackRate || 1, loop);
  } catch (e) {
    console.error('EffectsVN error', e);
  }
}

export function pauseEffectsVN() {
  if (!musicOn){
    return;
  }
  if (!effectsVNCtx) return;
  if (!isPlayingEffectsVN) return;
  effectsVNPauseAt = getEffectsVNCurrentPosition();
  try { effectsVNSource.onended = null; effectsVNSource.stop(0); } catch (e) {}
  effectsVNSource = null;
  isPlayingEffectsVN = false;
}

export function resumeEffectsVN() {
  if (!musicOn){
    return;
  }
  if (!effectsVNCtx || !effectsVNDecodedBuffer) return;
  if (isPlayingEffectsVN) return;
  createAndStartEffectsVNSource(effectsVNPauseAt, 0, effectsVN.playbackRate || 1, effectsVNSource ? effectsVNSource.loop : false);
}

export function stopEffectsVN() {
  if(!musicOn){
    return;
  }
  if (effectsVNSource) {
    try { effectsVNSource.onended = null; effectsVNSource.stop(0); } catch (e) {}
    effectsVNSource = null;
  }
  effectsVNPauseAt = 0;
  isPlayingEffectsVN = false;
}

export function setVolumeEffectsVN(value) {
  if(!musicOn){
    return;
  }
  const v = Math.max(0, Math.min(1, value));
  effectsVN.volume = v;
  if (effectsVNGain && effectsVNCtx) {
    effectsVNGain.gain.setValueAtTime(v, effectsVNCtx.currentTime);
  }
}

export async function transitionVolumeEffectsVN(targetVolume = 1, duration = 2000) {
  return new Promise(async (resolve) => {
    if(!musicOn){
      resolve();
      return;
    }
    targetVolume = Math.max(0, Math.min(1, targetVolume));
    const steps = 30;
    const interval = duration / steps;
    const start = effectsVN.volume != null ? effectsVN.volume : (effectsVNGain ? effectsVNGain.gain.value : 1);
    const step = (targetVolume - start) / steps;
    for (let i = 0; i < steps; i++) {
      const newV = Math.max(0, Math.min(1, start + step * (i + 1)));
      setVolumeEffectsVN(newV);
      await waitingFunc(interval);
    }
    setVolumeEffectsVN(targetVolume);
    resolve();
  });
}
